import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;  // send session_token cookie

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me. AuthCallback will run first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loadFavorites = useCallback(async () => {
    if (!user) { setFavorites(new Set()); return; }
    try {
      const { data } = await axios.get(`${API}/favorites`);
      setFavorites(new Set(data.favorites || []));
    } catch { setFavorites(new Set()); }
  }, [user]);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  const toggleFavorite = useCallback(async (symbol) => {
    if (!user) return false;
    const [from, to] = symbol.split("/");
    const isFav = favorites.has(symbol);
    // optimistic update
    setFavorites(prev => {
      const s = new Set(prev);
      if (isFav) s.delete(symbol); else s.add(symbol);
      return s;
    });
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${from}/${to}`);
      } else {
        await axios.post(`${API}/favorites`, { symbol });
      }
      return true;
    } catch {
      // rollback
      setFavorites(prev => {
        const s = new Set(prev);
        if (isFav) s.add(symbol); else s.delete(symbol);
        return s;
      });
      return false;
    }
  }, [favorites, user]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null); setFavorites(new Set());
  };

  return (
    <AuthContext.Provider value={{ user, loading, favorites, toggleFavorite, login, logout, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Handles the /...#session_id=... callback: exchanges once, then removes hash. */
export function useAuthCallback() {
  const { refreshUser } = useAuth();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    // Capture ?ref=CODE from URL and stash it so it survives the OAuth redirect.
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref) {
        localStorage.setItem("forex.ref", ref.trim().toUpperCase());
        url.searchParams.delete("ref");
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    } catch { /* ignore */ }

    const hash = window.location.hash;
    if (!hash?.includes("session_id=")) { setProcessed(true); return; }
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { setProcessed(true); return; }
    (async () => {
      try {
        let ref = null;
        try { ref = localStorage.getItem("forex.ref"); } catch { /* ignore */ }
        await axios.post(`${API}/auth/session`, { session_id: sid, ref });
        try { localStorage.removeItem("forex.ref"); } catch { /* ignore */ }
        await refreshUser();
      } catch { /* ignore */ }
      window.history.replaceState({}, document.title, window.location.pathname);
      setProcessed(true);
    })();
  }, [refreshUser]);

  return processed;
}
